import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, ArrowRight, Mail, Phone } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import secureflexLogo from "@/assets/secureflex-logo-new.png";
import ThemeToggle from "@/components/ThemeToggle";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Features", href: "#features" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Team", href: "#team" },
  { label: "Pricing", href: "#pricing" },
  { label: "FAQ", href: "#faq" },
  { label: "Resources", href: "#resources" },
  { label: "Contact", href: "#contact" },
  { label: "Apply", href: "#apply" },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
        <Link to="/" className="flex shrink-0 items-center gap-2">
          <img src={secureflexLogo} alt="SecureFlex Logo" className="h-14 w-14 sm:h-16 sm:w-16 object-contain drop-shadow-[0_0_12px_hsl(var(--primary)/0.5)]" />
          <div>
            <span className="text-xs sm:text-base font-bold tracking-wider text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              SECUREFLEX
            </span>
            <p className="hidden sm:block text-[9px] font-semibold tracking-[0.2em] text-primary">
              PROACTIVE DIGITAL FORTRESS
            </p>
          </div>
        </Link>

        <div className="hidden items-center gap-6 lg:flex">
          {navLinks.map((link) => (
            <a key={link.label} href={link.href} className="text-sm text-muted-foreground transition-colors hover:text-foreground">
              {link.label}
            </a>
          ))}
          <ThemeToggle />
          <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground">
            <Link to="/app">Sign In</Link>
          </Button>
          <Button size="sm" asChild className="gap-2 rounded-full">
            <Link to="/app/signup">
              Start Free Trial <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>

        <button className="p-2 -mr-2 text-foreground lg:hidden touch-manipulation" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
          {mobileOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="fixed inset-x-0 top-[60px] bottom-0 z-50 bg-background lg:hidden overflow-y-auto animate-fade-in border-t border-border/50">
          <div className="flex flex-col gap-2 p-6 pb-20">
            {navLinks.map((link) => (
              <a 
                key={link.label} 
                href={link.href} 
                className="py-4 text-lg font-medium text-muted-foreground border-b border-border/10 hover:text-foreground hover:bg-muted/5 transition-all" 
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <div className="pt-6 mt-4 space-y-4">
              <p className="text-[10px] font-bold tracking-[0.2em] text-primary uppercase">Direct Contact</p>
              <a href="mailto:secureflex@outlook.com" className="flex items-center gap-3 py-2 text-base text-muted-foreground hover:text-foreground">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <span className="truncate">secureflex@outlook.com</span>
              </a>
              <a href="tel:+27699223141" className="flex items-center gap-3 py-2 text-base text-muted-foreground hover:text-foreground">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Phone className="h-5 w-5 text-primary" />
                </div>
                <span>+27 69 922 3141</span>
              </a>
            </div>
            <div className="flex flex-col gap-3 mt-8">
              <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30">
                <span className="text-sm font-medium">Appearance</span>
                <ThemeToggle />
              </div>
              <Button variant="outline" size="lg" asChild className="w-full h-14 text-base rounded-xl font-bold">
                <Link to="/app" onClick={() => setMobileOpen(false)}>Sign In to Dashboard</Link>
              </Button>
              <Button size="lg" asChild className="w-full h-14 text-base rounded-xl font-bold gap-2 gold-glow">
                <Link to="/app/signup" onClick={() => setMobileOpen(false)}>
                  Start Free Trial <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
